import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import Button from "ui/Button";
import SectionHeading from "ui/SectionHeading";

import { OrganizationIntegrationsQuery } from "../../gql/Query.generated";
import { TestOutcome, testFailureMessage } from "../../Webhooks/WebhookForm";
import {
  useCreateIncidentIoEndpointMutation,
  useTestIncidentIoEndpointMutation,
  useUpdateIncidentIoEndpointMutation,
} from "../gql/Mutation.generated";

type IncidentIoEndpointType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["incidentIoEndpoints"][number];

const REFETCH_QUERIES = ["OrganizationIntegrations"];

function IncidentIoForm({
  organizationSlug,
  endpoint,
  onSaved,
  onCancel,
}: {
  organizationSlug: string;
  endpoint?: IncidentIoEndpointType;
  onSaved: (message: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(endpoint?.name ?? "");
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [replaceToken, setReplaceToken] = useState(!endpoint);
  const [token, setToken] = useState("");
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const [createEndpoint, createResult] = useCreateIncidentIoEndpointMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  const [updateEndpoint, updateResult] = useUpdateIncidentIoEndpointMutation({
    refetchQueries: REFETCH_QUERIES,
  });
  // Rejected before sending anything, e.g. rate limited or an invalid token
  const [testEndpoint, testResult] = useTestIncidentIoEndpointMutation({
    onError: (error) => setFormError(withChangesNotSaved(error.message)),
  });
  const busy =
    createResult.loading || updateResult.loading || testResult.loading;
  const tokenVariable = replaceToken ? token : undefined;
  // Renaming cannot break delivery; skip the test alert
  const deliveryConfigChanged =
    !endpoint || url.trim() !== endpoint.url || replaceToken;

  const handleSave = async () => {
    setFormError(null);

    if (deliveryConfigChanged) {
      const tested = await testEndpoint({
        variables: {
          organizationSlug,
          url,
          token: tokenVariable,
          incidentIoEndpointId: endpoint?.id,
        },
      });
      const result = tested.data?.testIncidentIoEndpoint;
      if (!result) {
        return;
      }
      if (result.status !== "succeeded") {
        setFormError(
          <TestOutcome result={result}>
            Test alert failed: {withChangesNotSaved(testFailureMessage(result))}
          </TestOutcome>,
        );
        return;
      }
    }

    const onError = (error: Error) =>
      setFormError(
        deliveryConfigChanged
          ? `Test alert sent, but saving the alert source failed: ${error.message}`
          : error.message,
      );
    const saved = endpoint
      ? await updateEndpoint({
          variables: {
            incidentIoEndpointId: endpoint.id,
            name,
            url,
            token: tokenVariable,
          },
          onError,
        })
      : await createEndpoint({
          variables: { organizationSlug, name, url, token },
          onError,
        });
    if (!saved.data) {
      return;
    }

    onSaved(
      deliveryConfigChanged
        ? "Test alert sent and alert source saved. Resolve the test alert in incident.io."
        : "incident.io alert source saved",
    );
  };

  const saveDisabledReason =
    name.trim() === ""
      ? "Enter a name for this alert source"
      : url.trim() === ""
      ? "Enter the alert source URL"
      : replaceToken && token.trim() === ""
      ? "Enter the alert source token"
      : undefined;

  const saveLabel = !deliveryConfigChanged
    ? "Save Alert Source"
    : endpoint
    ? "Test and Save"
    : "Test and Add";

  return (
    <div className="lg:min-w-[560px]">
      <p className="text-[#606771]">
        In incident.io, create an HTTP alert source and copy its URL and token
        below. For the token, you can paste the whole headers block incident.io
        shows. pganalyze sends an alert event when an issue is triggered,
        changes severity, or is resolved. Saving a new URL or token first sends
        a test alert to check them, which you can then resolve in incident.io.
      </p>
      <label className="flex flex-col font-normal">
        <SectionHeading>
          Name <span className="text-[#c00]">*</span>
        </SectionHeading>
        <input
          type="text"
          className="form-control"
          required
          aria-required
          placeholder="e.g. Database on-call"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </label>
      <label className="mt-4 flex flex-col font-normal">
        <SectionHeading>
          URL <span className="text-[#c00]">*</span>
        </SectionHeading>
        <input
          type="url"
          className="form-control"
          required
          aria-required
          placeholder="https://api.incident.io/v2/alert_events/http/…"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
        />
      </label>
      <div className="mt-4">
        {replaceToken ? (
          <label className="flex flex-col font-normal">
            <SectionHeading>
              Token <span className="text-[#c00]">*</span>
            </SectionHeading>
            <input
              type="password"
              className="form-control"
              required
              aria-required
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.currentTarget.value)}
            />
          </label>
        ) : (
          <>
            <SectionHeading>Token</SectionHeading>
            <input
              type="text"
              className="form-control"
              aria-label="Token (hidden)"
              value="••••••••"
              disabled
            />
            <Button
              className="mt-1"
              bare
              variant="link"
              onClick={() => setReplaceToken(true)}
            >
              Replace token
            </Button>
          </>
        )}
        <div className="mt-1 text-[#606771]">
          Stored encrypted and not shown again.
        </div>
      </div>
      {formError && (
        <div
          role="alert"
          className="mt-4 w-0 min-w-full break-words text-[#c93a2a]"
        >
          <FontAwesomeIcon className="mr-1" icon={faExclamationTriangle} />
          {formError}
        </div>
      )}
      <div className="mt-4">
        <span
          title={saveDisabledReason}
          className={
            saveDisabledReason
              ? "inline-block cursor-not-allowed"
              : "inline-block"
          }
        >
          <Button
            bare
            disabled={!!saveDisabledReason || busy}
            className={
              saveDisabledReason
                ? "btn btn-success pointer-events-none"
                : "btn btn-success"
            }
            onClick={() => {
              void handleSave();
            }}
          >
            {testResult.loading ? "Sending test alert…" : saveLabel}
          </Button>
        </span>
        <Button
          bare
          className="text-link hover:text-link-hover ml-3"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function withChangesNotSaved(message: string) {
  return `${message.replace(/\.$/, "")}. Changes not saved.`;
}

export default IncidentIoForm;
